import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { OrderSettings as OrderSettingsType } from '../../../types/storeSettings';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';

interface OrderSettingsProps {
  settings: OrderSettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

const OrderSettings: React.FC<OrderSettingsProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const { updateSection } = useStoreSettings();
  const [formData, setFormData] = useState<OrderSettingsType>(settings);
  const [editing, setEditing] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      onSave(true);
      onError(null);

      await updateSection('order', formData);

      onSuccess(true);
      setEditing(false);
    } catch (error) {
      onError('حدث خطأ أثناء حفظ إعدادات الطلبات');
    } finally {
      onSave(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          إعدادات الطلبات
        </Typography>

        {!editing ? (
          <Button
            startIcon={<EditIcon />}
            onClick={() => setEditing(true)}
            variant="outlined"
          >
            تعديل
          </Button>
        ) : (
          <Button
            type="submit"
            startIcon={<SaveIcon />}
            variant="contained"
            color="primary"
          >
            حفظ التغييرات
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="الحد الأدنى للطلب"
            name="minimumOrderAmount"
            type="number"
            value={formData.minimumOrderAmount}
            onChange={handleChange}
            disabled={!editing}
            InputProps={{
              startAdornment: <InputAdornment position="start">ج.م</InputAdornment>,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="الحد الأقصى لعدد المنتجات في الطلب"
            name="maximumOrderItems"
            type="number"
            value={formData.maximumOrderItems}
            onChange={handleChange}
            disabled={!editing}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="بادئة رقم الطلب"
            name="orderNumberPrefix"
            value={formData.orderNumberPrefix}
            onChange={handleChange}
            disabled={!editing}
            helperText="مثال: ORD-"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="فترة إلغاء الطلب (بالساعات)"
            name="orderCancellationPeriod"
            type="number"
            value={formData.orderCancellationPeriod}
            onChange={handleChange}
            disabled={!editing}
            helperText="الفترة التي يمكن للعميل خلالها إلغاء الطلب"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="فترة الإرجاع (بالأيام)"
            name="returnPeriod"
            type="number"
            value={formData.returnPeriod}
            onChange={handleChange}
            disabled={!editing}
            helperText="الفترة التي يمكن للعميل خلالها إرجاع المنتج"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.allowGuestCheckout}
                onChange={handleChange}
                name="allowGuestCheckout"
                disabled={!editing}
              />
            }
            label="السماح بالشراء للزوار (بدون تسجيل)"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.requirePhoneNumber}
                onChange={handleChange}
                name="requirePhoneNumber"
                disabled={!editing}
              />
            }
            label="طلب رقم الهاتف إلزامي"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.autoConfirmOrders}
                onChange={handleChange}
                name="autoConfirmOrders"
                disabled={!editing}
              />
            }
            label="تأكيد الطلبات تلقائيًا"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="سياسة الإرجاع"
            name="returnPolicy"
            value={formData.returnPolicy}
            onChange={handleChange}
            disabled={!editing}
            multiline
            rows={4}
            helperText="سياسة الإرجاع التي سيتم عرضها للعملاء"
          />
        </Grid>
      </Grid>

      {editing && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setFormData(settings);
              setEditing(false);
            }}
            sx={{ mr: 2 }}
          >
            إلغاء
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            حفظ التغييرات
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default OrderSettings;
